import { createZodDto } from "nestjs-zod/dto";
import { z } from "zod";

import { jobBoardSchema } from "../job-search.dto";

const dateFilterSchema = z
  .string()
  .trim()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine((value) => {
    const [year, month, day] = value.split("-").map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));

    return (
      date.getUTCFullYear() === year &&
      date.getUTCMonth() === month - 1 &&
      date.getUTCDate() === day
    );
  }, "Invalid date.");

/**
 * Every value of the Prisma `ApplicationStatus` enum.
 *
 * Kept exhaustive on purpose: this schema parses `status` on every application
 * the API returns, so a value present in the database and missing here is a row
 * the client cannot read at all. It is also the fallback branch of
 * `expandStatusFilter` on the server, which is what makes a raw enum value
 * usable as a filter without a board column existing for it yet.
 */
export const applicationStatusSchema = z.enum([
  "TAILORING",
  "REVIEWING",
  "QUEUED",
  "APPLYING",
  "APPLIED",
  "FAILED",
  "SKIPPED",
  "INTERVIEW",
  "VIEWED",
  "SCREEN",
  "OFFER",
  "REJECTED",
  "GHOSTED",
  "NEEDS_ATTENTION",
  "ACCEPTED",
  "WITHDRAWN",
  "EXPIRED",
]);

/**
 * States a USER may move an application to.
 *
 * This must stay in lockstep with `TRACKER_STATUSES` in the server's
 * application.service.ts, because `allowedTrackerStatusesFor` derives BOTH
 * directions from that array: a status missing there can never be entered and,
 * once entered, can never be left. A value in one gate and not the other is
 * either a PATCH that passes validation and then 400s, or a move the UI offers
 * that the API refuses.
 *
 * QUEUED was listed here and never belonged to the user — the server has no
 * transition INTO a pipeline state, so `PATCH { status: "QUEUED" }` passed this
 * schema and was then rejected by the service. The two gates disagreed; the
 * fix is to drop it, not to teach the service to accept it.
 *
 * WITHDRAWN and EXPIRED are here because a user must be able to CLOSE an
 * application that never reached an employer without claiming it did. They are
 * carved out of the appliedAt/submittedVia stamp in `updateStatus` and out of
 * RESPONDED_APPLICATION_STATUSES for exactly that reason.
 */
export const trackableApplicationStatusSchema = z.enum([
  "APPLIED",
  "VIEWED",
  "SCREEN",
  "INTERVIEW",
  "OFFER",
  "REJECTED",
  "GHOSTED",
  "ACCEPTED",
  "WITHDRAWN",
  "EXPIRED",
]);

/**
 * States a MANUALLY LOGGED application may be created in.
 *
 * Narrower than `trackableApplicationStatusSchema` on purpose. `logManual`
 * unconditionally stamps `appliedAt` and `submittedVia` on the row it creates —
 * the whole point of "+ Log application" is the user telling us they sent
 * something. WITHDRAWN and EXPIRED mean the exact opposite (nothing was ever
 * sent), so logging one would fabricate a delivery on the very row created to
 * say a delivery never happened. There is no "log an application I never made"
 * flow; closing an existing card is what the tracker move is for.
 */
export const loggableApplicationStatusSchema = z.enum([
  "APPLIED",
  "VIEWED",
  "SCREEN",
  "INTERVIEW",
  "OFFER",
  "REJECTED",
  "GHOSTED",
  "ACCEPTED",
]);

/**
 * `submittedVia` values that mean THE SYSTEM delivered the application.
 *
 * THE VOCABULARY IS THE FACT. Three genuinely different things collapse into
 * "Applied" on the board and keep every downstream number misleading:
 *
 *   (a) delivered — our automation, or the extension acting for us, put this
 *       in front of an employer and observed it land;
 *   (b) self-reported — the user told us they sent it themselves;
 *   (c) merely saved — tailored, queued, blocked, never sent at all.
 *
 * `submittedVia` already separates all three (`null` is (c)), which is why
 * this list exists rather than a parallel column: `outreach.service.ts` reads
 * the field as "a human actually sent something" and `daily-report.service.ts`
 * counts it as a submission, so inventing a second delivery signal would give
 * the board and the Telegram digest two different truths.
 *
 * RESERVED — a client may never write one of these. `POST /applications/manual`
 * accepts a caller-supplied `submittedVia` (Co-Pilot tags it `COPILOT_WORKDAY`
 * and friends), and without this deny-list any authenticated caller could post
 * `LINKEDIN_EASY_APPLY_EXTENSION` and manufacture a delivery the product never
 * performed. Deny-by-default against an explicit set, never a heuristic like
 * `endsWith("_APPLY")` — a heuristic silently admits the next platform tag
 * somebody adds.
 */
export const SYSTEM_DELIVERED_SUBMITTED_VIA = [
  "LINKEDIN_EASY_APPLY",
  "LEVER_APPLY",
  "INDEED_APPLY",
  "GREENHOUSE_APPLY",
  "WORKABLE_APPLY",
  "LINKEDIN_EASY_APPLY_EXTENSION",
] as const;

export type SystemDeliveredSubmittedVia = (typeof SYSTEM_DELIVERED_SUBMITTED_VIA)[number];

const SYSTEM_DELIVERED_SET = new Set<string>(SYSTEM_DELIVERED_SUBMITTED_VIA);

export const isSystemDeliveredSubmittedVia = (value: string | null | undefined): boolean =>
  typeof value === "string" && SYSTEM_DELIVERED_SET.has(value);

/**
 * How this application reached — or failed to reach — an employer.
 *
 * Computed server-side from `submittedVia` so the client never re-parses the
 * string. It was already being re-parsed in at least two places with different
 * rules, which is how a Co-Pilot self-assertion ended up counted as an
 * automated submission in the daily report.
 */
export const applicationDeliveryStateSchema = z.enum([
  /** Our automation or the extension delivered it and observed it land. */
  "SYSTEM_DELIVERED",
  /** The user told us they sent it (manual log, Co-Pilot, "I applied myself"). */
  "SELF_REPORTED",
  /** Nothing was ever sent: saved, tailored, queued, blocked, or closed unsent. */
  "NOT_DELIVERED",
]);

export type ApplicationDeliveryStateDto = z.infer<typeof applicationDeliveryStateSchema>;

/**
 * WHY this application is sitting in the To-do column, as a stable token.
 *
 * Derived server-side from the stored `errorMessage`, because that message is
 * free English written by whichever code path gave up: the client cannot match
 * on it, cannot translate it, and must not GUESS at it. The board groups To-do
 * cards by this, and the grouping has to state only what the row actually
 * carries — the two dominant reasons in the production backlog are a missing
 * extension and a board with no delivery path, and neither is a quota problem.
 * Labelling them "daily limit reached" would be a true-sounding upsell that is
 * false, and neither waiting nor upgrading frees a single one of those cards.
 */
export const applicationBlockedReasonSchema = z.enum([
  /**
   * Spec 051 — prepared and waiting for the user to open the extension and
   * press Start run. NOT a failure: the work is intact and deliverable.
   *
   * Unlike every other member here, this one is derived from the ROW
   * (`status === QUEUED && heldForExtensionAt != null`), not from matching
   * `errorMessage` text — a held row has no error message, because nothing
   * went wrong.
   */
  "AWAITING_EXTENSION_RUN",
  /** No live extension session, so the LinkedIn hand-off could not run. */
  "EXTENSION_UNAVAILABLE",
  /** We have no delivery path for this board on this account at all. */
  "AUTOMATION_UNAVAILABLE",
  /** The posting redirects to the employer's own site; only a human can finish it. */
  "EXTERNAL_SITE",
  /** A real quota stop — the ONLY bucket where "limit" is an honest word. */
  "LIMIT_REACHED",
  /** Stored board credentials are missing or no longer valid. */
  "CREDENTIALS_INVALID",
  /** Something failed that none of the above describes; show the stored text. */
  "OTHER",
]);

export type ApplicationBlockedReasonDto = z.infer<typeof applicationBlockedReasonSchema>;

/**
 * The three board columns.
 *
 * Deliberately three. "To do" is not a drop target — the server has no
 * transition INTO a pipeline state — and the optional stage columns
 * (Viewed / Screening / Interview / Offer) are deferred because nothing on the
 * server writes VIEWED, SCREEN or OFFER, and INTERVIEW's only writer is Gmail
 * sync, which has no connect UI. Shipping them would make this a manual CRM
 * before one pipeline signal is real.
 */
export const applicationBoardColumnSchema = z.enum(["todo", "applied", "closed"]);

export type ApplicationBoardColumnDto = z.infer<typeof applicationBoardColumnSchema>;

export class CreateApplicationDto extends createZodDto(
  z.object({
    jobId: z.string().trim().min(1),
    jobProfileId: z.string().trim().min(1),
    skipTailoring: z.boolean().optional(),
    autoSubmit: z.boolean().optional(),
  }),
) {}

/**
 * Mark an EXISTING feed job as already applied (manually, on the original
 * posting). Records an APPLIED application for that jobId so it moves to the
 * Applications "Applied" column and drops out of the jobs feed.
 */
export class MarkJobAppliedDto extends createZodDto(
  z.object({
    jobId: z.string().trim().min(1),
    jobProfileId: z.string().trim().min(1).optional(),
  }),
) {}

export class LogManualApplicationDto extends createZodDto(
  z.object({
    role: z.string().trim().min(1),
    company: z.string().trim().min(1),
    status: loggableApplicationStatusSchema.default("APPLIED"),
    source: jobBoardSchema.default("OTHER"),
    url: z.string().trim().url().optional(),
    jobProfileId: z.string().trim().min(1).optional(),
    /**
     * Phase 1 — Co-Pilot tag for SubmittedViaCard (PR-G/3) attribution.
     * When the Co-Pilot sidebar mounts on a third-party ATS (Workday,
     * iCIMS, etc.) and the user clicks "Mark as submitted", it posts
     * here with submittedVia like "COPILOT_WORKDAY" so the dashboard
     * drawer shows the indigo Compass attribution. Optional —
     * defaults to "manual" for legacy "+ Log application" flow.
     */
    submittedVia: z
      .string()
      .trim()
      .max(64)
      .regex(/^[A-Z_]+$/, "submittedVia must be uppercase + underscores (e.g. COPILOT_WORKDAY)")
      /**
       * A client may tag WHO it was, never claim the SYSTEM did it.
       *
       * This route is guarded only by TwoFactorGuard, so before this refine any
       * authenticated caller could post `submittedVia:
       * "LINKEDIN_EASY_APPLY_EXTENSION"` and mint a delivery that never
       * happened — and it would have been believed everywhere, because
       * `outreach.service.ts` and `daily-report.service.ts` both read this
       * field as proof an application reached an employer.
       */
      .refine((value) => !isSystemDeliveredSubmittedVia(value), {
        message: "submittedVia is reserved for submissions the system performed itself.",
      })
      .optional(),
  }),
) {}

export class UpdateApplicationDto extends createZodDto(
  z.object({
    notes: z.string().trim().optional(),
    coverLetter: z.string().trim().optional(),
    tailoredResumeData: z.record(z.unknown()).optional(),
  }),
) {}

export class ApplicationFilterDto extends createZodDto(
  z.object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(20),
    status: z.string().trim().optional(),
    board: z.string().trim().optional(),
    company: z.string().trim().optional(),
    jobProfileId: z.string().trim().min(1).optional(),
    /**
     * `"true"` keeps only applications that carry tailoring history (a tailored
     * output, or a row still in the tailoring pipeline) and drops the
     * tracker-only rows a user creates by hand ("Mark as applied", "Log
     * application"). Only the value `"true"` is accepted so a stray
     * `tailored=false` can never be coerced into the opposite filter.
     */
    tailored: z.enum(["true"]).optional(),
    dateFrom: dateFilterSchema.optional(),
    dateTo: dateFilterSchema.optional(),
    sortBy: z.string().trim().default("createdAt"),
    sortOrder: z.enum(["asc", "desc"]).default("desc"),
  }),
) {}

export class BulkApproveDto extends createZodDto(
  z.object({
    applicationIds: z.array(z.string().trim().min(1)).min(1).max(50),
  }),
) {}

export class UpdateStatusDto extends createZodDto(
  z.object({
    status: trackableApplicationStatusSchema,
  }),
) {}

export const applicationJobSchema = z.object({
  id: z.string(),
  title: z.string(),
  company: z.string(),
  location: z.string().nullable().default(null),
  board: jobBoardSchema,
  aiScore: z.number().int().nullable().default(null),
  url: z.string().url(),
  /**
   * When the EMPLOYER posted it — not when we found it.
   *
   * `discoveredAt` is the tempting fallback and it is wrong: a six-month-old
   * posting saved through the extension this morning would read as fresh. Null
   * means unknown, and unknown must stay unknown (see `isPostingOld`).
   */
  postedAt: z.coerce.date().nullable().default(null),
});

export type ApplicationJobDto = z.infer<typeof applicationJobSchema>;

export const applicationJobProfileSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export type ApplicationJobProfileDto = z.infer<typeof applicationJobProfileSchema>;

export const applicationListItemSchema = z.object({
  id: z.string(),
  status: applicationStatusSchema,
  job: applicationJobSchema,
  jobProfile: applicationJobProfileSchema,
  tailoredSummary: z.string().nullable().default(null),
  coverLetter: z.string().nullable().default(null),
  appliedAt: z.coerce.date().nullable().default(null),
  respondedAt: z.coerce.date().nullable().default(null),
  notes: z.string().nullable().default(null),
  errorMessage: z.string().nullable().default(null),
  retryExhausted: z.boolean().default(false),
  /**
   * True when tailoring produced a validated résumé but no usable cover letter.
   * The card stays in review; the server refuses every delivery path for it.
   */
  tailoringPartial: z.boolean().default(false),
  /**
   * Whether the user may retry tailoring. Decided by the server with the same
   * policy the retry endpoint enforces, so the button and the API cannot disagree.
   */
  canRetryTailoring: z.boolean().default(false),
  // Spec 033 — surfaces the submission source in the list view so the
  // app-drawer label can render the per-submitter copy variant without
  // an additional GET /applications/:id round-trip.
  submittedVia: z.string().nullable().default(null),
  /**
   * Delivered / self-reported / never-sent, decided by the server.
   *
   * The board's close popover picks its MODE from this: a card that never
   * reached an employer is asked "what happened?" (I applied myself / posting
   * closed / no longer interested), a delivered one is asked "what was the
   * outcome?". Getting that backwards is expensive — offering "Rejected" on a
   * never-delivered card fabricates both a delivery and an employer reply, and
   * neither `respondedAt` nor `submittedVia` has a prior value to restore.
   */
  deliveryState: applicationDeliveryStateSchema.default("NOT_DELIVERED"),
  /**
   * Why this card is stuck, as a token the client can group and translate.
   * Null when nothing is wrong with it. Never regex the `errorMessage` for
   * this — that text is free English and changes without notice.
   */
  blockedReason: applicationBlockedReasonSchema.nullable().default(null),
  /** True only when durable execution authority is waiting for user reconciliation. */
  uncertainOutcome: z.boolean().default(false),
  /**
   * The tailored PDF, if one was rendered. NOT present on every card: in the
   * production backlog 288 of 297 stuck rows had a tailored resume but only
   * 223 had a rendered PDF, because `preparePdfForExtensionClaim` swallows its
   * own failures. Render the action conditionally; do not promise it.
   */
  resumePdfUrl: z.string().nullable().default(null),
  /**
   * The posting is older than the badge threshold. States AGE only — 40% of
   * "expired" LinkedIn postings sampled 2026-08-07 were still accepting
   * applications, so this must never be rendered as "closed". False whenever
   * `job.postedAt` is null.
   */
  isPostingOld: z.boolean().default(false),
  /**
   * Tracker states this application may be moved to, computed server-side.
   *
   * The mechanism that keeps the board honest. Previously the client decided
   * which cards were draggable and the server kept a private adjacency table,
   * and they disagreed: the UI rendered TAILORING, REVIEWING, APPLYING,
   * NEEDS_ATTENTION and FAILED rows inside the "Applied" column while the
   * server rejected every move out of them. On production 2026-08-31 that was
   * ~512 of 542 cards silently refusing to be dragged.
   *
   * Now one server policy feeds both the read and the write path, so the UI
   * cannot offer a move the API will reject — it does not invent the list.
   * Defaulted so an older client, or a cached response, degrades to "no drag"
   * rather than to a guess.
   */
  allowedTrackerStatuses: z.array(applicationStatusSchema).default([]),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type ApplicationListItemDto = z.infer<typeof applicationListItemSchema>;

export const applicationSchema = applicationListItemSchema.extend({
  jobProfile: applicationJobProfileSchema.extend({
    baseResumeId: z.string().optional(),
  }),
  tailoredResumeData: z.record(z.unknown()).nullable().default(null),
  resumePdfUrl: z.string().nullable().default(null),
  confirmationScreenshotUrl: z.string().nullable().default(null),
  errorScreenshotUrl: z.string().nullable().default(null),
  submittedVia: z.string().nullable().default(null),
  responseType: z.string().nullable().default(null),
  retryCount: z.number().int(),
});

export type ApplicationDto = z.infer<typeof applicationSchema>;

export const createApplicationResponseSchema = z.object({
  id: z.string(),
  status: applicationStatusSchema,
  jobId: z.string(),
  jobProfileId: z.string(),
  createdAt: z.coerce.date(),
});

export type CreateApplicationResponseDto = z.infer<typeof createApplicationResponseSchema>;

export const bulkApproveResponseSchema = z.object({
  approved: z.array(z.string()),
  skippedDueToLimit: z.array(z.string()),
  errors: z.array(z.string()),
});

export type BulkApproveResponseDto = z.infer<typeof bulkApproveResponseSchema>;

/**
 * Per-column totals for the board headers.
 *
 * Sourced from the applications module, NEVER from `GET /stats` — that whole
 * controller sits behind `createEntitlementGuard("advancedAnalytics")`, which
 * is ULTIMATE-only, so every FREE and PRO user would get a 403 and see every
 * column header render 0.
 *
 * Independent of the current status filter and of pagination on purpose: the
 * headers must say how many cards exist, not how many fit in the page being
 * looked at. `KanbanColumn` rendering `applications.length` off a 20-row
 * default page is how the counts got wrong in the first place.
 */
export const applicationBoardCountsSchema = z.object({
  todo: z.number().int().default(0),
  applied: z.number().int().default(0),
  closed: z.number().int().default(0),
});

export type ApplicationBoardCountsDto = z.infer<typeof applicationBoardCountsSchema>;

export const paginatedApplicationsSchema = z.object({
  items: z.array(applicationListItemSchema),
  meta: z.object({
    total: z.number().int(),
    page: z.number().int(),
    limit: z.number().int(),
    totalPages: z.number().int(),
  }),
  counts: applicationBoardCountsSchema,
});

export type PaginatedApplicationsDto = z.infer<typeof paginatedApplicationsSchema>;

/**
 * What actually happened when the user pressed "Try one now" on ONE card.
 *
 * There is no bulk retry and there must not be: `retry()` rejects every
 * NEEDS_ATTENTION row, a re-queue re-enters `handOffForManualApply` and lands
 * straight back, and at PRO's 5/day the ~280-card backlog is 56 days of quota
 * — more than the monthly pool. One card, one honest answer.
 *
 * The outcome is a TOKEN, not a sentence: the client owns the copy so it can
 * be translated, and the server owns the fact so the copy cannot drift from
 * it. Two of these outcomes are decided synchronously and change nothing —
 * that is the point. Queueing a card whose delivery path does not exist parks
 * it for a claim that never comes, and 5 minutes later the sweep flips it back
 * with a refund and a push notification, which is a worse answer arriving
 * later.
 */
export const applicationTrySubmissionOutcomeSchema = z.enum([
  /** Queued, and the EXTENSION will do it — but only when the user presses Apply now. */
  "QUEUED_FOR_EXTENSION",
  /**
   * Queued, and the SERVER will attempt a real submission to a real employer.
   * Materially different from the extension path and worth saying out loud.
   */
  "QUEUED_FOR_SERVER",
  /** Nothing queued: this board needs the extension and no session is live. */
  "EXTENSION_UNAVAILABLE",
  /** Nothing queued: this application permanently failed. */
  "RETRY_EXHAUSTED",
  /** Nothing queued: still inside its backoff window. `retryAvailableAt` says when. */
  "RETRY_NOT_AVAILABLE_YET",
  /** Nothing queued: this status is not one we can retry from. */
  "NOT_RETRYABLE",
]);

export type ApplicationTrySubmissionOutcomeDto = z.infer<
  typeof applicationTrySubmissionOutcomeSchema
>;

export const applicationTrySubmissionResultSchema = z.object({
  applicationId: z.string(),
  outcome: applicationTrySubmissionOutcomeSchema,
  /** Status of the row AFTER the attempt. Unchanged for every non-queued outcome. */
  status: applicationStatusSchema,
  /** The row's OWN stored reason, verbatim. Never a sentence invented here. */
  errorMessage: z.string().nullable().default(null),
  blockedReason: applicationBlockedReasonSchema.nullable().default(null),
  retryAvailableAt: z.coerce.date().nullable().default(null),
});

export type ApplicationTrySubmissionResultDto = z.infer<
  typeof applicationTrySubmissionResultSchema
>;

export const applicationStatsSchema = z.object({
  totalApplied: z.number().int(),
  totalQueued: z.number().int(),
  totalReviewing: z.number().int(),
  responseRate: z.number(),
  interviewRate: z.number(),
  activeThisWeek: z.number().int(),
  averageDaysToResponse: z.number(),
  applicationsUsedToday: z.number().int(),
  applicationsLimitToday: z.number().int(),
  ghostedCount: z.number().int(),
  applicationsToday: z.number().int().default(0),
  avgScore: z.number().int().default(0),
  queueSize: z.number().int().default(0),
});

export type ApplicationStatsDto = z.infer<typeof applicationStatsSchema>;
